/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/*
	 	class Cache

	 	A template class which implements the LRU cache mechanism.

	 	This class uses two generic types 'K' and 'V' where K is refered for 'key'
	 	and 'V' is refered for 'value' to store items as key-value pair in cache.
	 */

// An item to store in the cache
interface CacheItem<K, V> {
    key: K;
    value: V;
    size: number; // size in bytes.
}

export class LRUCache<K, V> {
    // An ordered map to store references to the items for faster searching.
    private mMap: Map<K, CacheItem<K, V>>;
    // Max cache size in bytes
    private mMaxCapacity: number;
    // Current cache size in bytes
    private mCurCacheSize: number;

    // Returns true if cache is empty, else false
    private isEmpty(): boolean {
        return this.mMap.size == 0;
    }

    constructor(capacity: number) {
        this.mMap = new Map<K, CacheItem<K, V>>();
        this.mMaxCapacity = capacity;
        this.mCurCacheSize = 0;
    }

    // Get an entry from the cache using 'key', if present.
    // Returns 'fail' if not present.
    protected get(key: K, fail: V): V {
        if (!this.find(key)) {
            // if map doesn't contain the item
            return fail;
        }
        // Found the key in cache, hence move it to the front of the map.
        const cacheItem = this.mMap.get(key)!;
        this.mMap.delete(key);
        this.mMap.set(key, cacheItem);
        return cacheItem.value;
    }

    // Add a 'value' of 'size' using 'key' to the front of the list.
    // Returns true for successful insertion.
    // It will return false, if there is no space to add in cache
    protected addToFront(key: K, value: V, size: number): boolean {
        if (this.find(key)) {
            this.moveToFront(key);
            return true;
        }

        // Check if we have enough space to add it to the cache
        if (!this.haveSpace(size)) {
            return false;
        }

        const newItem: CacheItem<K, V> = { key: key, value: value, size: size };
        if (!newItem) return false;

        // Add new entry to front of the map.
        this.mMap.set(key, newItem);
        this.mCurCacheSize += size;
        return true;
    }

    // Remove an item associated with key [if present] from cache.
    protected remove(key: K): void {
        if (!this.find(key)) {
            return;
        }
        const item: CacheItem<K, V> = this.mMap.get(key)!;
        this.mCurCacheSize -= item.size;
        this.mMap.delete(key);
    }

    // Get a rear item (CacheItem) from the map
    // returns undefined if the map is empty, else rear item.
    protected getRear(): CacheItem<K, V> | undefined {
        if (this.isEmpty()) {
            return undefined;
        }
        const key = this.mMap.keys().next().value;
        return this.mMap.get(key);
    }

    // Returns the maximum capacity of cache in bytes
    protected maxCacheSize(): number {
        return this.mMaxCapacity;
    }

    // Returns true if the cache is having an item (CacheItem) associated with 'key'
    find(key: K): boolean {
        return this.mMap.has(key);
    }

    // Returns true if cache is having enough space to hold 'size',
    // else returns false.
    haveSpace(size: number): boolean {
        return this.mCurCacheSize + size <= this.mMaxCapacity;
    }

    getCacheSize(): number {
        return this.mCurCacheSize;
    }

    // Increase or decrease the cache max-capacity by 'size' param.
    // To decrease cache max-capacity, 'size' should be negative.
    addToMaxCapacity(size: number): void {
        // if ((int)mMaxCapacity > abs(size))
        this.mMaxCapacity += size;
    }

    getSize(key: K): number {
        let size = 0;
        const cacheItem = this.mMap.get(key);
        if (cacheItem != undefined) {
            size = cacheItem.size;
        }
        return size;
    }

    // Move the item (CacheItem) associated with 'key' to front of the map.
    // Check whether the item is present or not in cache before calling the function.
    moveToFront(key: K): boolean {
        let lStatus = false;
        if (this.find(key)) {
            const item: CacheItem<K, V> = this.mMap.get(key)!;
            this.mMap.delete(key);
            this.mMap.set(key, item);
            lStatus = true;
        }
        return lStatus;
    }
}
